/**
 * @fileoverview Home page component
 * @module features/home/pages/HomePage
 */

import { ReactElement, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { PackagePlus } from 'lucide-react'
import { DashboardDataProvider } from '@/shared/providers'
import { useFilterMenuSlot } from '@/shared/contexts/FilterMenuSlotContext'
import { TagsFilterExtension } from '@/shared/components/filters/extensions/TagsFilterExtension'
import { MetricCards } from '@/shared/components/ui/MetricCards'
import { ProfitOverTimeChart } from '@/shared/components/ui/ProfitOverTimeChart'
import { AdsPerformanceOverTimeChart } from '@/shared/components/ui/AdsPerformanceOverTimeChart'
import { ProfitByCountry } from '@/shared/components/ui/ProfitByCountry'
import { TopSkusByProfitChart } from '@/shared/components/ui/TopSkusByProfitChart'
import {
  CostVsProfitDonut,
  AdPlatformSpendDonut,
  ScrollReveal,
} from '@/shared/components/ui'
import { Button } from '@/components/ui/button'
import { getProducts } from '@/lib/productService'
import { useFilter } from '@/shared/hooks/useFilter'
import { toDateOnlyStringLocal } from '@/shared/utils/dateUtils'
import { OrderFeed } from '@/features/daily-summary/components/OrderFeed'

/**
 * Home page component displaying the main landing page
 *
 * @component
 * @example
 * ```tsx
 * <HomePage />
 * ```
 */
export function HomePage(): ReactElement {
  return (
    <DashboardDataProvider>
      <HomePageContent />
    </DashboardDataProvider>
  )
}

function HomePageContent(): ReactElement {
  const { t } = useTranslation('features-home')
  const navigate = useNavigate()
  const { startDate, endDate } = useFilter()
  const { setSlot, clearSlot } = useFilterMenuSlot()

  // Inject the tag filter into the global FlyingMenu slot. Selecting a tag
  // updates FilterContext.tags, which DashboardDataProvider threads into the
  // statistics queries powering the metric cards and charts on this page.
  useEffect(() => {
    setSlot({ content: <TagsFilterExtension /> })
    return () => clearSlot()
  }, [setSlot, clearSlot])

  const isExactlyToday = useMemo(() => {
    if (!startDate || !endDate) return false
    const todayStr = toDateOnlyStringLocal(new Date())
    return (
      toDateOnlyStringLocal(startDate) === todayStr &&
      toDateOnlyStringLocal(endDate) === todayStr
    )
  }, [startDate, endDate])

  const productsQuery = useQuery({
    queryKey: ['products', { page: 0, size: 1, sort: 'revenue' }],
    queryFn: () => getProducts({ page: 0, size: 1, sort: 'revenue' }),
    staleTime: 5 * 60 * 1000,
  })

  // Only show empty state when we have definitively confirmed 0 products
  const hasConfirmedNoProducts =
    productsQuery.isSuccess &&
    productsQuery.data?.content?.length === 0

  if (hasConfirmedNoProducts) {
    return (
      <div className="h-[calc(100dvh-6rem)] flex items-center justify-center px-4">
        <ScrollReveal blur>
          <div className="flex flex-col items-center gap-4 sm:gap-6 text-center max-w-md">
            <div className="size-16 sm:size-24 rounded-full bg-muted/50 flex items-center justify-center">
              <PackagePlus className="size-8 sm:size-12 text-muted-foreground" />
            </div>
            <div className="space-y-1.5 sm:space-y-2">
              <h2 className="text-xl sm:text-2xl font-semibold text-foreground">
                {t('emptyState.title')}
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground">
                {t('emptyState.description')}
              </p>
            </div>
            <Button
              size="lg"
              onClick={() => navigate('/dashboard/management/products')}
              className="gap-2"
            >
              <PackagePlus className="size-4" />
              {t('emptyState.action')}
            </Button>
          </div>
        </ScrollReveal>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8 space-y-8">
        {/* Primary Business Metrics */}
        <ScrollReveal blur>
          <MetricCards />
        </ScrollReveal>

        {/* Live Order Feed — only when filter is exactly today */}
        {isExactlyToday && (
          <ScrollReveal blur duration={0.3}>
            <OrderFeed />
          </ScrollReveal>
        )}

        {/* Charts and content - updates instantly on filter change */}
        <div className="space-y-8">

          {/* Profit Over Time */}
          <ScrollReveal delay={0.1} blur>
            <ProfitOverTimeChart />
          </ScrollReveal>

          {/* Cost Breakdown & Ad Platform Analysis */}
          <ScrollReveal delay={0.1} blur>
            <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-8">
              <CostVsProfitDonut/>
              <AdPlatformSpendDonut/>
            </div>
          </ScrollReveal>

          {/* Country */}
          <ScrollReveal delay={0.1} blur>
            <ProfitByCountry />
          </ScrollReveal>

          {/* Top SKUs */}
          <ScrollReveal delay={0.1} blur>
            <TopSkusByProfitChart />
          </ScrollReveal>

          {/* Ads performance */}
          <ScrollReveal delay={0.1} blur>
            <AdsPerformanceOverTimeChart />
          </ScrollReveal>
        </div>
      </div>
    </div>
  )
}
